/**
 * @fileoverview `DrawSurface`: one dedicated PyMOL object per BMaps surface.
 *
 * PyMOL colours a surface from the colours of its atoms, which in the atom objects are the
 * stick / cartoon colours (cartoon atoms even carry their residue's chain colour). So a surface
 * is NOT a representation of the atom objects. Each `DrawSurface()` builds its own object
 * (`_bm_srf<n>`) holding copies of the *computing* atoms; only the *display* atoms show the
 * `surface` representation, coloured by `colorFn`:
 *
 *   computing atoms  -> the atoms of the object: PyMOL's surface is computed over all of them
 *                       (surface_mode 0 = every atom without the `ignore` flag; the flag is
 *                       cleared, PyMOL sets it on solvent / ions when it classifies a load)
 *   display atoms    -> `show surface` on those only: the patches of the other atoms stay hidden
 *   colorFn(atom)    -> the copy's atom colour = the colour of its surface patch; evaluated when
 *                       DrawSurface() is called, as 3Dmol does (setSyncSurface(true)), for every
 *                       computing atom: PyMOL blends the colours of neighbouring atoms into a
 *                       patch, hidden ones included
 *   opacity          -> object-level `transparency` = 1 - opacity (3Dmol's default 0.8)
 *   not pickable     -> `pick_surface 0`: PyMOL then leaves a transparent surface out of the pick
 *                       pass, so a click reaches the (invisible) hotspot atoms inside a cloud
 *                       (VERIFIED on payload 0.1.0); 3Dmol's surfaces are not clickable either
 *
 * The copies are written as CIF from the BMaps atoms (no bonds, no secondary structure) with
 * ids of their own: the object depends on nothing else, so it survives the removal of its atoms
 * and a Reset(), exactly like a 3Dmol surface mesh, until RemoveSurface().
 */

import {
    ObjectPrefix, ObjectKinds, SurfaceSolventRadius, repMask,
} from './PyMOLConstants';
import { cifFromAtoms } from './PyMOLCifWriter';

function newStats() {
    return {
        loaded: 0, deleted: 0, atoms: 0, errors: 0, createdNames: [],
    };
}

export class PyMOLSurfaces {
    /**
     * @param {object} options
     * @param {() => number} options.allocateId a fresh atom id (unique across all objects)
     */
    constructor({ allocateId }) {
        this.allocateId = allocateId;
        /** @type {Map<number, object>} surface id -> record */
        this.surfaces = new Map();
        this.deadNames = [];
        this.seq = 0;
        this.nextSurfaceId = 1;
        this.repOrder = null;
        this.lastStats = newStats();
    }

    get size() { return this.surfaces.size; }

    has(id) { return this.surfaces.has(id); }

    get(id) { return this.surfaces.get(id) || null; }

    /**
     * @param {object} spec
     * @param {object[]} spec.computingAtoms BMaps atoms the surface is computed over
     * @param {Set<object>} spec.displayAtoms BMaps atoms whose patches are shown
     * @param {(atom: object) => number} spec.colorOf 0xRRGGBB of an atom's patch
     * @param {number} spec.opacity 0..1
     * @returns {number} the surface id (synchronous)
     */
    add({
        computingAtoms, displayAtoms, colorOf, opacity,
    }) {
        const id = this.nextSurfaceId++;
        this.seq++;
        const ids = new Map();
        const atoms = [];
        for (const atom of computingAtoms) {
            if (ids.has(atom)) continue;
            ids.set(atom, this.allocateId());
            atoms.push(atom);
        }
        const displayIds = [];
        for (const atom of atoms) {
            if (displayAtoms.has(atom)) displayIds.push(ids.get(atom));
        }
        this.surfaces.set(id, {
            id,
            name: `${ObjectPrefix}${ObjectKinds.surface}${this.seq}`,
            atoms,
            ids,
            allIds: Int32Array.from(atoms, (atom) => ids.get(atom)),
            displayIds: Int32Array.from(displayIds),
            colors: Uint32Array.from(atoms, (atom) => colorOf(atom)),
            opacity: Math.max(0, Math.min(1, opacity)),
            loaded: false, // flushed
            inPyMOL: false, // the object exists
        });
        return id;
    }

    remove(id) {
        const record = this.surfaces.get(id);
        if (!record) return false;
        this.surfaces.delete(id);
        if (record.inPyMOL) this.deadNames.push(record.name);
        return true;
    }

    /** Names of the surface objects PyMOL holds, oldest first. */
    loadedNames() {
        return [...this.surfaces.values()]
            .filter((record) => record.inPyMOL)
            .map((record) => record.name);
    }

    isSurfaceName(name) {
        for (const record of this.surfaces.values()) if (record.name === name) return true;
        return false;
    }

    setRepOrder(order) { this.repOrder = order; }

    flush(viewer) {
        const stats = newStats();
        this.lastStats = stats;
        for (const name of this.deadNames) {
            this.guard(stats, `delete ${name}`, () => { viewer.delete(name); stats.deleted++; });
        }
        this.deadNames = [];
        for (const record of this.surfaces.values()) {
            if (record.loaded) continue;
            this.guard(stats, `load ${record.name}`, () => this.load(viewer, record, stats));
        }
        return stats;
    }

    guard(stats, what, fn) {
        try {
            fn();
        } catch (ex) {
            stats.errors++;
            console.error(`PyMOL backend: surface: ${what} failed`, ex);
        }
    }

    load(viewer, record, stats) {
        record.loaded = true; // also on failure: never retried every flush
        if (record.displayIds.length === 0) return; // nothing to show (3Dmol: an empty mesh)
        const cif = cifFromAtoms(record.atoms, {
            idOf: (atom) => record.ids.get(atom),
            name: record.name,
            ssOf: () => 'L',
            bonds: false,
            oneModel: true,
        });
        if (cif.atomCount === 0) return;
        viewer.bridge.load_cif(record.name, cif.text, 0);
        record.inPyMOL = true;
        const { name } = record;
        viewer.cmd.hide('everything', name);
        viewer.cmd.flag('ignore', name, 'clear', { quiet: 1 });
        viewer.cmd.set('pick_surface', 0, name);
        viewer.cmd.set('solvent_radius', SurfaceSolventRadius, name);
        if (record.opacity < 1) viewer.cmd.set('transparency', 1 - record.opacity, name);
        viewer.bridge.apply_colors(name, record.allIds, record.colors);
        viewer.bridge.apply_reps(name, record.displayIds, repMask(['surface'], this.repOrder || undefined), 0);
        stats.loaded++;
        stats.createdNames.push(name);
        stats.atoms += cif.atomCount;
    }
}
